import * as THREE from 'three';
import { FikoThreeJsComponent } from '../../fiko-three-js.component';

/**
 * ULTRA PERFORMANS: Dynamic Resolution Scaling
 * Automatically reduces render target resolution when FPS drops below target
 * Maintains quality while ensuring smooth 60 FPS
 *
 * Strategy:
 * - Monitor actual FPS
 * - If FPS < 60: reduce resolution by 20-30%
 * - If FPS > 60: gradually increase resolution back up
 * - Smooth transitions to avoid visual popping
 */
export class FikoDynamicResolutionScaler {
  private renderer: THREE.WebGLRenderer;
  private targetFPS: number = 60;
  private minResolutionScale: number = 0.5; // Minimum 50% resolution
  private maxResolutionScale: number = 1.0; // Maximum 100% resolution
  private currentScale: number = 1.0;

  private frameCount: number = 0;
  private frameTimes: number[] = [];
  private maxFrameSamples: number = 30;
  private checkInterval: number = 15; // Check FPS every 15 frames

  private targetFrameTime: number = 16.67; // 60 FPS = 16.67ms per frame
  private scaleChangeRate: number = 0.02; // Change scale by 2% per check

  private lastFrameTime: number = 0; // performance.now() timestamp
  private originalRenderTarget: THREE.RenderTarget | null = null;
  private scaledRenderTarget: THREE.RenderTarget | null = null;
  private screenQuad: THREE.Mesh | null = null;

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
    this.targetFrameTime = 1000 / this.targetFPS;
    this.lastFrameTime = performance.now();
    this.setupScreenQuad();
  }

  /**
   * Setup full-screen quad for upscaling/downscaling
   */
  private setupScreenQuad(): void {
    const geometry = new THREE.BufferGeometry();
    const vertices = new Float32Array([
      -1, 1, 0,
      1, 1, 0,
      1, -1, 0,
      -1, -1, 0
    ]);
    const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);

    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(new THREE.BufferAttribute(indices, 1));

    const material = new THREE.RawShaderMaterial({
      vertexShader: `
        precision highp float;
        attribute vec3 position;
        void main() {
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;
        uniform sampler2D inputTexture;
        uniform vec2 inputSize;
        void main() {
          vec2 uv = gl_FragCoord.xy / inputSize;
          gl_FragColor = texture2D(inputTexture, uv);
        }
      `,
      uniforms: {
        inputTexture: { value: null },
        inputSize: { value: new THREE.Vector2(1, 1) }
      }
    });

    this.screenQuad = new THREE.Mesh(geometry, material);
    this.screenQuad.frustumCulled = false;
  }

  /**
   * Update frame timing and adjust resolution if needed
   * Call this at START of render loop (before rendering)
   * Optional: deltaTime parameter (used as fallback if provided)
   */
  updateFrameTiming(deltaTime?: number): void {
    const now = performance.now();
    this.frameCount++;

    // Calculate actual frame time from performance.now()
    let frameTime = now - this.lastFrameTime;

    // If calculated frame time is invalid, use provided deltaTime or default
    if (frameTime <= 0 || frameTime > 1000 || !isFinite(frameTime)) {
      // Fallback: use provided deltaTime if valid
      if (deltaTime && deltaTime > 0 && deltaTime < 1000) {
        frameTime = deltaTime;
      } else {
        // Last resort: estimate ~60 FPS
        frameTime = 16.67;
      }
    }

    this.lastFrameTime = now;
    this.frameTimes.push(frameTime);

    if (this.frameTimes.length > this.maxFrameSamples) {
      this.frameTimes.shift();
    }

    // Check FPS every N frames
    if (this.frameCount % this.checkInterval === 0) {
      this.checkAndAdjustResolution();
    }
  }

  /**
   * Calculate average FPS from recent frame times
   */
  private getAverageFPS(): number {
    if (this.frameTimes.length === 0) return 60;

    const avgTime = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;

    // Validate: avgTime must be positive
    if (avgTime <= 0 || !isFinite(avgTime)) {
      return 60; // Fallback to 60 FPS if invalid
    }

    const fps = 1000 / avgTime;

    // Sanity check: FPS should be between 1 and 999
    if (fps < 1 || fps > 999 || !isFinite(fps)) {
      return 60; // Fallback if result is invalid
    }

    return fps;
  }

  /**
   * Adjust resolution based on FPS
   */
  private checkAndAdjustResolution(): void {
    const currentFPS = this.getAverageFPS();
    const newScale = this.calculateNewScale(currentFPS);

    if (Math.abs(newScale - this.currentScale) > 0.01) {
      const oldScale = this.currentScale;
      this.currentScale = newScale;

      // Log scale change decision
      const direction = newScale > oldScale ? '⬆️ UP' : '⬇️ DOWN';
      const oldPercent = Math.round(oldScale * 100);
      const newPercent = Math.round(newScale * 100);
      FikoThreeJsComponent.debugLog(`${direction} Resolution: ${oldPercent}% → ${newPercent}% (FPS: ${Math.round(currentFPS)})`);

      this.applyResolutionScale();
    }
  }

  /**
   * Calculate target scale based on FPS
   * - If FPS < 50: aggressive scale down
   * - If FPS < 60: moderate scale down
   * - If FPS > 60: gradual scale up
   */
  private calculateNewScale(fps: number): number {
    let targetScale = this.currentScale;

    if (fps < 50) {
      // Aggressive downscaling
      targetScale -= this.scaleChangeRate * 2;
    } else if (fps < this.targetFPS) {
      // Moderate downscaling
      targetScale -= this.scaleChangeRate;
    } else if (fps > this.targetFPS + 10) {
      // Gradual upscaling (only when well above target)
      targetScale += this.scaleChangeRate * 0.5;
    }

    const constrained = Math.max(this.minResolutionScale, Math.min(this.maxResolutionScale, targetScale));

    // FPS threshold checks (logging removed for production)

    return constrained;
  }

  /**
   * Apply resolution scale to renderer
   */
  private applyResolutionScale(): void {
    const size = new THREE.Vector2();
    this.renderer.getSize(size);

    const scaledWidth = Math.floor(size.x * this.currentScale);
    const scaledHeight = Math.floor(size.y * this.currentScale);

    // Log resolution change
    const fps = Math.round(this.getAverageFPS());
    const scalePercent = Math.round(this.currentScale * 100);
    FikoThreeJsComponent.debugLog(`📊 Dynamic Resolution: ${scalePercent}% (${scaledWidth}x${scaledHeight}) @ ${fps}FPS`);

    // Update render target if needed
    if (!this.scaledRenderTarget ||
        this.scaledRenderTarget.width !== scaledWidth ||
        this.scaledRenderTarget.height !== scaledHeight) {

      if (this.scaledRenderTarget) {
        this.scaledRenderTarget.dispose();
      }

      this.scaledRenderTarget = new THREE.WebGLRenderTarget(scaledWidth, scaledHeight, {
        format: THREE.RGBAFormat,
        type: THREE.UnsignedByteType,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter
      });
    }

  }

  /**
   * Get current resolution scale (0.5 - 1.0)
   */
  getResolutionScale(): number {
    return this.currentScale;
  }

  /**
   * Get current FPS
   */
  getCurrentFPS(): number {
    return this.getAverageFPS();
  }

  /**
   * Get stats for display
   */
  getStats(): {
    fps: number;
    scale: number;
    scaledResolution: { width: number; height: number };
  } {
    const size = new THREE.Vector2();
    this.renderer.getSize(size);

    return {
      fps: Math.round(this.getAverageFPS()),
      scale: this.currentScale,
      scaledResolution: {
        width: Math.floor(size.x * this.currentScale),
        height: Math.floor(size.y * this.currentScale)
      }
    };
  }

  /**
   * Set target FPS (default 60)
   */
  setTargetFPS(fps: number): void {
    this.targetFPS = fps;
    this.targetFrameTime = 1000 / fps;
  }

  /**
   * Set minimum resolution scale (default 0.5 = 50%)
   */
  setMinResolutionScale(scale: number): void {
    this.minResolutionScale = Math.max(0.25, Math.min(1.0, scale));
  }

  /**
   * Cleanup
   */
  dispose(): void {
    if (this.scaledRenderTarget) {
      this.scaledRenderTarget.dispose();
    }
    if (this.screenQuad) {
      (this.screenQuad.geometry as THREE.BufferGeometry).dispose();
      (this.screenQuad.material as THREE.Material).dispose();
    }
    this.frameTimes = [];
  }
}
